import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, MapPin, Truck, PackageCheck, Star, ChevronRight, MessageCircle, Search, PackageSearch, Gift, Tag, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { TESTIMONIALS, HERO_IMG, IMG_HANDOFF, IMG_WAREHOUSE } from "../mock";
import { useApp } from "../context/AppContext";
import { ICONS, mediaUrl } from "../api";
import { useNavigate } from "react-router-dom";
import { CONTACT_TEXT, waUrl } from "../waHelper";

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0; const dur = 1400; const t0 = performance.now();
        const step = (t) => {
          const p = Math.min((t - t0) / dur, 1);
          setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return <span ref={ref}>{n}{suffix}</span>;
}

export default function Home() {
  const { settings, corridors, services, countries, reviews, warehouses, siteImages, offers, t } = useApp();
  const renderHighlighted = (str) => str.split(/(\{[^}]+\})/).map((part, i) =>
    part.startsWith("{") && part.endsWith("}")
      ? <span key={i} className="orange-gradient-text">{part.slice(1, -1)}</span>
      : <React.Fragment key={i}>{part}</React.Fragment>);
  const activeOffers = (offers || []).filter((o) => o.active !== false);
  const [showOffers, setShowOffers] = useState(false);
  useEffect(() => {
    if (activeOffers.length && !sessionStorage.getItem("sl_offers_seen")) {
      const t = setTimeout(() => setShowOffers(true), 600);
      return () => clearTimeout(t);
    }
  }, [activeOffers.length]);
  const closeOffers = () => { setShowOffers(false); sessionStorage.setItem("sl_offers_seen", "1"); };
  const heroImg = mediaUrl(siteImages.hero) || HERO_IMG;
  const handoffImg = mediaUrl(siteImages.handoff) || IMG_HANDOFF;
  const warehouseImg = mediaUrl(siteImages.warehouse) || IMG_WAREHOUSE;
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const results = q ? countries.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : [];
  const testimonials = reviews.length ? reviews.slice(0, 4) : TESTIMONIALS;
  const stats = [
    { value: countries.length || 60, suffix: "+", label: t("stats.countries") },
    { value: warehouses.length, suffix: "", label: t("stats.warehouses") },
    { value: 24, suffix: "/7", label: t("stats.support") },
    { value: 100, suffix: "%", label: t("stats.insured") },
  ];

  return (
    <div>
      {/* WELCOME OFFERS POPUP */}
      {showOffers && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-up" data-testid="offers-popup" onClick={closeOffers}>
          <div className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-white rounded-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button data-testid="offers-popup-close" onClick={closeOffers} aria-label="Close offers"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 grid place-items-center transition-colors">
              <X className="w-5 h-5 text-neutral-700" />
            </button>
            <div className="orange-gradient px-8 pt-8 pb-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-20" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white/20 grid place-items-center mx-auto mb-3 backdrop-blur"><Gift className="w-7 h-7 text-white" /></div>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-white">{t("offers.heading")}</h2>
                <p className="text-white/90 mt-2 text-sm md:text-base">{t("offers.sub")}</p>
              </div>
            </div>
            <div className="p-6 md:p-8 -mt-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {activeOffers.slice(0, 4).map((o) => (
                  <div key={o.id} className="glass rounded-2xl p-5 flex flex-col" data-testid={`offer-popup-${o.id}`}>
                    {o.tag && <span className="inline-flex w-fit items-center gap-1 text-[11px] font-semibold uppercase tracking-wider orange-gradient text-white px-2.5 py-1 rounded-full"><Tag className="w-3 h-3" /> {o.tag}</span>}
                    <h3 className="font-display font-semibold text-lg mt-3">{o.title}</h3>
                    <p className="text-neutral-600 text-sm mt-2 leading-relaxed flex-1">{o.desc}</p>
                    <a href={`https://wa.me/${settings.whatsapp}?text=${encodeURIComponent("Hi! I'd like to claim the offer: " + o.title)}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 mt-4 font-medium"><MessageCircle className="w-4 h-4" /> {t("offers.claim")}</a>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 justify-center mt-7">
                <Button className="orange-gradient text-white border-0 gap-2 hover:opacity-90" onClick={() => { closeOffers(); nav("/send"); }}>{t("offers.send")} <ArrowRight className="w-4 h-4" /></Button>
                <Button variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2" onClick={() => { closeOffers(); nav("/offers"); }}><Gift className="w-4 h-4 text-orange-500" /> {t("offers.seeAll")}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden grid-bg">
        <div className="hero-aurora" />
        <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-32 -right-24 w-[36rem] h-[36rem] rounded-full bg-orange-600/20 blur-[120px] animate-float-slow" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full bg-amber-400/10 blur-[130px]" />
        <div className="absolute -bottom-32 -left-24 w-[30rem] h-[30rem] rounded-full bg-orange-500/10 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center relative">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-medium glass rounded-full px-4 py-1.5 text-orange-400 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" /> {t("hero.badge")}
            </span>
            <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight">
              {renderHighlighted(t("hero.title"))}
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-send-button" className="orange-gradient text-white font-semibold h-12 px-6 border-0 hover:opacity-90 gap-2" onClick={() => nav("/send")}>
                {t("hero.send")} <ArrowRight className="w-4 h-4" />
              </Button>
              <Button data-testid="hero-inquiry-button" variant="outline" className="h-auto min-h-12 px-6 py-3 border-orange-200 text-neutral-800 hover:bg-orange-50 gap-2 whitespace-normal" onClick={() => nav("/inquiry")}><MessageCircle className="w-4 h-4 text-orange-600 shrink-0" />{t("inquiry.nav")}</Button>
              <Button data-testid="hero-track-button" variant="outline" className="h-12 px-6 border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2" onClick={() => nav("/track")}>
                <PackageSearch className="w-4 h-4 text-orange-500" /> {t("hero.track")}
              </Button>
              <a data-testid="hero-whatsapp-link" href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-12 px-6 border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2">
                  <MessageCircle className="w-4 h-4 text-orange-500" /> {t("hero.whatsapp")}
                </Button>
              </a>
            </div>

            {/* Country search */}
            <div className="mt-10 max-w-md">
              <label className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{t("hero.searchLabel")}</label>
              <div className="mt-2 glass rounded-xl flex items-center px-4 h-12 focus-within:border-orange-500/60 transition-colors">
                <Search className="w-4 h-4 text-neutral-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hero.searchPlaceholder")}
                  className="bg-transparent flex-1 px-3 text-sm outline-none placeholder:text-neutral-300" />
              </div>
              {q && (
                <div className="mt-2 glass rounded-xl p-2 max-h-52 overflow-auto">
                  {results.length ? results.map((c) => (
                    <button key={c.name} onClick={() => nav("/send")}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-black/[0.04] text-sm">
                      <span className="flex items-center gap-2"><span className="text-lg">{c.flag}</span> {c.name}</span>
                      <span className="text-green-500 text-xs flex items-center gap-1"><PackageCheck className="w-3.5 h-3.5" /> Available</span>
                    </button>
                  )) : <p className="px-3 py-2 text-sm text-neutral-500">{t("hero.noMatch")}</p>}
                </div>
              )}
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-3xl overflow-hidden glass p-2 orange-glow">
              <img src={heroImg} alt="SmartLogistics worldwide delivery" className="rounded-2xl w-full h-[420px] object-contain bg-white" />
            </div>
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 flex items-center gap-3 shadow-xl">
              <div className="w-11 h-11 rounded-xl orange-gradient grid place-items-center"><Truck className="w-5 h-5 text-white" /></div>
              <div><p className="text-sm font-semibold">{t("hero.livePickup")}</p><p className="text-xs text-neutral-600">{t("hero.courierWay")}</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/10 bg-neutral-100/60">
        <div className="max-w-7xl mx-auto px-5 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display font-bold text-4xl md:text-5xl orange-gradient-text"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="text-sm text-neutral-600 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center mb-14">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">How it works</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">Three simple steps</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MessageCircle, t: "1. Share details", d: "Fill the parcel form or WhatsApp us the sender & receiver details." },
            { icon: HomeIcon, t: "2. We pick up", d: "Our courier collects the parcel from your doorstep at a time that suits you." },
            { icon: PackageCheck, t: "3. Doorstep delivery", d: "We deliver safely to the receiver's door and keep you updated all the way." },
          ].map((s) => (
            <div key={s.t} className="glass card-hover rounded-2xl p-7">
              <div className="w-12 h-12 rounded-xl orange-gradient grid place-items-center mb-5"><s.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display font-semibold text-xl">{s.t}</h3>
              <p className="text-neutral-600 text-sm mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CORRIDORS */}
      <section className="max-w-7xl mx-auto px-5 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Our routes</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">Popular delivery corridors</h2>
          </div>
          <Link to="/coverage"><Button variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2">View all coverage <ChevronRight className="w-4 h-4" /></Button></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {corridors.map((c) => (
            <div key={c.id} className="glass card-hover rounded-2xl p-6 relative">
              {c.tag && <span className="absolute top-5 right-5 text-[10px] font-semibold uppercase tracking-wider orange-gradient text-white px-2.5 py-1 rounded-full">{c.tag}</span>}
              <div className="flex items-center gap-3 text-3xl mb-4">{c.from} <ArrowRight className="w-5 h-5 text-orange-500" /> {c.to}</div>
              <h3 className="font-display font-semibold text-lg">{c.name}</h3>
              <p className="text-neutral-600 text-sm mt-2 leading-relaxed">{c.desc}</p>
              <p className="text-xs text-orange-400 mt-4 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Est. {c.days}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-3xl overflow-hidden glass p-2">
            <img src={handoffImg} alt="Courier handing parcel" className="rounded-2xl w-full h-[400px] object-cover" />
          </div>
          <div>
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">What we offer</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">Premium logistics, built for people</h2>
            <p className="text-neutral-600 mt-4 leading-relaxed">From door pickup to insured delivery, every step is handled with care and precision.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {services.slice(0, 4).map((s) => { const Icon = ICONS[s.icon] || ICONS["package-check"]; return (
                <div key={s.id || s.title} className="flex gap-3">
                  <div className="w-10 h-10 rounded-lg glass grid place-items-center shrink-0"><Icon className="w-5 h-5 text-orange-500" /></div>
                  <div><p className="font-semibold text-sm">{s.title}</p><p className="text-xs text-neutral-600 mt-1">{s.desc}</p></div>
                </div>
              ); })}
            </div>
            <Link to="/services"><Button className="orange-gradient text-white border-0 mt-8 gap-2 hover:opacity-90">Explore all services <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="text-center mb-12">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Testimonials</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">Loved by families worldwide</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {testimonials.map((t) => (
            <div key={t.id || t.name} className="glass rounded-2xl p-6 card-hover">
              <div className="flex gap-0.5 mb-4">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />)}</div>
              <p className="text-sm text-neutral-700 leading-relaxed">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full orange-gradient grid place-items-center text-white text-xs font-bold">{t.name.charAt(0)}</div>
                <div><p className="text-sm font-semibold">{t.name}</p><p className="text-xs text-neutral-500">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="relative rounded-3xl overflow-hidden orange-gradient p-10 md:p-16 orange-glow">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-white">Ready to send your parcel home?</h2>
            <p className="text-white/90 mt-4 text-lg">Share your details in under 2 minutes. We'll pick up from your door and handle the rest.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button className="bg-white text-orange-600 hover:bg-white/90 font-semibold h-12 px-6 gap-2" onClick={() => nav("/send")}>Get Started <ArrowRight className="w-4 h-4" /></Button>
              <a href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-12 px-6 border-white/40 text-white hover:bg-black/[0.05] gap-2 bg-transparent"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
              </a>
            </div>
          </div>
          <img src={warehouseImg} alt="" className="hidden lg:block absolute right-0 top-0 h-full w-1/3 object-cover opacity-25" />
        </div>
      </section>
    </div>
  );
}
